/* eslint-disable relay/unused-fields */
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";

import { useSearchContext } from "scmp-app/components/search/contexts";
import { query } from "scmp-app/components/search/search-result-panel";
import { Typeform } from "scmp-app/lib/typeform";
import type { searchResultListArticleSearchQuery$key } from "scmp-app/queries/__generated__/searchResultListArticleSearchQuery.graphql";
import type { searchResultListSectionSearchConnection$key } from "scmp-app/queries/__generated__/searchResultListSectionSearchConnection.graphql";
import type { searchResultListTopicSearchConnection$key } from "scmp-app/queries/__generated__/searchResultListTopicSearchConnection.graphql";
import type { searchResultPanelQuery } from "scmp-app/queries/__generated__/searchResultPanelQuery.graphql";

import { DateRangeMenu } from "./advanced-filter-options/date-range-menu";
import { PlusCheckbox } from "./advanced-filter-options/plus-checkbox";
import { SectionMenu } from "./advanced-filter-options/section-menu";
import { TypeMenu } from "./advanced-filter-options/type-menu";
import { ArticleSearchResultList } from "./article-search-result-list";
import { SectionSearchResultList } from "./section-search-result-list";
import {
  AdvancedFilterOptionsContainer,
  FilterWrapper,
  HeaderContainer,
  NoArticleResultsContent,
  NoResultsContainer,
  NoResultsContent,
  NoResultsHeader,
  ResultAndSortingContainer,
  ResultText,
  StyledSearchResultListSortingMenu,
  TotalCount,
  TotalResultText,
} from "./styles";
import { TopicSearchResultList } from "./topic-search-result-list";

export type Props = {
  queryReference: PreloadedQuery<searchResultPanelQuery>;
};

export const SearchResultList: FunctionComponent<Props> = ({ queryReference }) => {
  const data = usePreloadedQuery(query, queryReference);

  const articles = useFragment<searchResultListArticleSearchQuery$key>(
    graphql`
      fragment searchResultListArticleSearchQuery on Query {
        articleSearchV2(
          filter: {
            query: $query
            sectionIds: $sectionIds
            articleTypes: $articleTypeIds
            paywallTypeIds: $paywallTypeIds
            contentTypes: $contentTypes
            startDate: $startDate
            endDate: $endDate
          }
          orderBy: $orderBy
          first: $first
          after: $after
        ) {
          connection {
            edges {
              node {
                __typename
              }
            }
            totalCount
          }
        }
        ...articleSearchResultListQuery
      }
    `,
    data,
  );

  const topics = useFragment<searchResultListTopicSearchConnection$key>(
    graphql`
      fragment searchResultListTopicSearchConnection on TopicSearchConnection {
        edges {
          node {
            __typename
          }
        }
        ...topicSearchResultListTopicSearchConnection
      }
    `,
    data.topicSearch,
  );

  const sections = useFragment<searchResultListSectionSearchConnection$key>(
    graphql`
      fragment searchResultListSectionSearchConnection on SectionSearchConnection {
        edges {
          node {
            __typename
          }
        }
        ...sectionSearchResultListSectionSearchConnection
      }
    `,
    data.sectionSearch,
  );

  const noResult = useMemo(
    () =>
      [
        articles.articleSearchV2?.connection.edges.length,
        topics?.edges.length,
        sections?.edges.length,
      ].every(edgeLength => !edgeLength),
    [
      articles.articleSearchV2?.connection.edges.length,
      sections?.edges.length,
      topics?.edges.length,
    ],
  );

  const { currentValue } = useSearchContext();

  if (noResult) {
    return (
      <NoResultsContainer>
        <NoResultsHeader>Sorry! There are no results for your search term.</NoResultsHeader>
        <NoResultsContent>
          Please check if your spelling is correct, or try a different word or phrase.
        </NoResultsContent>
      </NoResultsContainer>
    );
  }

  return (
    <>
      <ErrorBoundary fallback={<></>}>
        <ClientSideSuspense fallback={<></>}>
          <Typeform />
        </ClientSideSuspense>
      </ErrorBoundary>
      <HeaderContainer>
        <AdvancedFilterOptionsContainer>
          <FilterWrapper>
            <DateRangeMenu />
            <SectionMenu queryReference={queryReference} />
            <TypeMenu queryReference={queryReference} />
          </FilterWrapper>
          <PlusCheckbox />
        </AdvancedFilterOptionsContainer>
        <ResultAndSortingContainer>
          <TotalResultText>
            <TotalCount>
              {articles.articleSearchV2?.connection.totalCount?.toLocaleString()}
            </TotalCount>
            {" results "}
            for <ResultText>&ldquo;{currentValue}&rdquo;</ResultText>
          </TotalResultText>
          <StyledSearchResultListSortingMenu />
        </ResultAndSortingContainer>
      </HeaderContainer>

      {sections && <SectionSearchResultList reference={sections} />}
      {topics && <TopicSearchResultList reference={topics} />}
      {articles.articleSearchV2?.connection.totalCount ? (
        <ArticleSearchResultList reference={articles} />
      ) : (
        <NoArticleResultsContent>No results</NoArticleResultsContent>
      )}
    </>
  );
};

SearchResultList.displayName = "SearchResultList";
